import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  apiKey,
  contentStackGraphqlFreshUri,
  CONTENT_TTL_MS,
  environment,
} from './config';

let gqlCache = new InMemoryCache();
let gqlCacheCreatedAt = Date.now();

const restCache: {
  [key: string]: { res: AxiosResponse<any>; cachedAt: number };
} = {};

const access_token = process?.env?.CONTENTSTACK_DELIVERY_TOKEN as string;

const getGqlCache = () => {
  if (Date.now() - gqlCacheCreatedAt > CONTENT_TTL_MS) {
    gqlCache = new InMemoryCache();
    gqlCacheCreatedAt = Date.now();
  }

  return gqlCache;
};

// Creates an Apollo client for the Contentstack GraphQL delivery API. The
// endpoint URI is resolved per query with a time-bucketed cache buster so no
// cache layer (Contentstack's CDN included) serves content older than
// CONTENT_TTL_MS. Outside development, results are also shared across
// requests via a TTL-bound in-memory cache.
export const ContentStackClient = () =>
  new ApolloClient({
    cache: environment !== 'development' ? getGqlCache() : new InMemoryCache(),
    link: new HttpLink({
      uri: () => contentStackGraphqlFreshUri(),
      headers: {
        access_token,
      },
    }),
  });

// Performs a GET request against the Contentstack REST delivery API. Outside
// development, responses are cached across requests for CONTENT_TTL_MS; the
// same time bucket is sent as a query param to defeat upstream CDN caching.
export const ContentStackRESTRequest = (
  config: AxiosRequestConfig
): Promise<AxiosResponse<any>> => {
  return new Promise((resolve, reject) => {
    const cacheKey = JSON.stringify(config);
    const cached = restCache[cacheKey];

    if (
      cached &&
      Date.now() - cached.cachedAt <= CONTENT_TTL_MS &&
      environment !== 'development'
    ) {
      return resolve(cached.res);
    }

    axios({
      ...config,
      url: 'https://cdn.contentstack.io/v3' + config.url,
      method: 'GET',
      params: {
        environment,
        fresh: Math.floor(Date.now() / CONTENT_TTL_MS),
        ...config?.params,
      },
      headers: {
        access_token,
        api_key: apiKey,
      },
    })
      .then((res) => {
        restCache[cacheKey] = { res, cachedAt: Date.now() };
        resolve(res);
      })
      .catch(reject);
  });
};
